import {
  onBeforeMount,
  onBeforeUnmount,
  onMounted,
  computed,
  nextTick,
  ref,
  toRef,
  watch,
} from 'vue';
import { useIsFetching, useIsMutating } from '@tanstack/vue-query';
import { storeToRefs } from 'pinia';
import { useRoute } from 'vue-router';
import { isNil, snakeCase, toUpper, upperCase } from 'lodash-es';
import { useDatabaseOverviewStore } from '@/database/stores/database-overview';
import { useUserEnvStore } from '@/common/stores/user-env';
import type {
  LastSelectedInfo,
  LastSelectedInfoByViewType,
  LastSelectedMultiView,
  LastSelectedSingleView,
  OverviewType,
  SupportEnvValue,
  InstanceInfoViewType,
  OverviewTimePeriod,
} from '@/database/utils/overview.types';
import { TimeRangeInfo, type DBType, type MetricRequest } from '@/common/utils/types';
import { USER_ENV_KEYS } from '@/common/define/userEnv.define';
import { OVERVIEW_TYPE } from '../utils/overview.define';
import { useDatabaseMetaStore } from '@/database/stores/database-meta';
import { DB_TYPE } from '@/common/define/common.define';
import { useInternational } from '@/common/locale';
import { useMessage } from '@/common/inject/useMessage';
import { getTimeRangeFromPeriod, isValidDbType, isValidViewType } from './overview.utils';
import { InstanceInfo } from './pa/pa.types';
import { CLUSTER_DIFF_KEY } from './define';
import { getRtmSelectedInfo } from './utils';

interface RefreshTriggerParams {
  fetchCallback?: (() => Promise<void>) | null;
  fetchInitialData?: (() => Promise<void>) | null;
}

const getEnabledInstance =
  (dbType: string) =>
  (instance: { type?: DBType; enabled?: boolean }): boolean => {
    if (dbType) {
      return instance.type === dbType && !!instance.enabled;
    }
    return !!instance.enabled;
  };

export const useOverviewRefreshTrigger = (params: RefreshTriggerParams = {}) => {
  const { fetchCallback, fetchInitialData } = params;

  const route = useRoute();
  const overviewStore = useDatabaseOverviewStore();
  const { currentViewInfo, lastFetchedTime, selectedPeriodInfo } = storeToRefs(overviewStore);

  const isLoading = ref<boolean>(false);

  const registeredCallback: RefreshTriggerParams = { fetchCallback, fetchInitialData };

  const setCallback = (callbackParams?: RefreshTriggerParams) => {
    if (!callbackParams) {
      registeredCallback.fetchCallback = null;
      registeredCallback.fetchInitialData = null;
      return;
    }

    registeredCallback.fetchCallback = callbackParams.fetchCallback ?? fetchCallback;
    registeredCallback.fetchInitialData = callbackParams.fetchInitialData ?? fetchInitialData;
  };

  const fetchViewData = async () => {
    isLoading.value = false;
    if (!registeredCallback.fetchCallback) {
      return;
    }
    await registeredCallback.fetchCallback();
  };

  const fetchViewInitialData = async () => {
    isLoading.value = true;
    await registeredCallback.fetchInitialData?.();
    await fetchViewData();
    isLoading.value = false;
  };

  watch(lastFetchedTime, async (curr, prev) => {
    if (curr && prev && curr !== prev) {
      await fetchViewData();
    }
  });

  watch(
    () => route.fullPath,
    async () => {
      // NOTE: 스토어 매핑 시점 이후에 데이터 초기화 진행
      await nextTick();
      fetchViewInitialData();
    },
    { immediate: true },
  );

  onBeforeUnmount(() => {
    setCallback();
  });

  return {
    isLoading: toRef(isLoading, 'value'),
    currentViewInfo,
    selectedPeriodInfo,
    setCallback,
  };
};

export const useDatabaseUserEnv = () => {
  const envStore = useUserEnvStore();

  const getUserEnv = <T extends SupportEnvValue>(key: string): T | null => {
    return envStore.getEnvValue<T>(key);
  };

  const setUserEnv = async <T extends SupportEnvValue>({
    key,
    value,
  }: {
    key: string;
    value: T;
  }) => {
    await envStore.setEnvValue<T>([{ key, value }]);
  };

  return {
    setUserEnv,
    getUserEnv,
  };
};

export const useLastSelected = () => {
  const { setUserEnv, getUserEnv } = useDatabaseUserEnv();

  const lastSelected = ref<Record<string, any>>({});

  const initLastSelected = () => {
    lastSelected.value = {};
  };

  const getOverviewSelectedIdKey = ({
    dbType,
    viewType,
  }: {
    dbType: NonNullable<DBType>;
    viewType: OverviewType;
  }): string => {
    // ex. MYSQL_SINGLE_VIEW_SELECTED_ID
    const key = `${upperCase(dbType)}_${toUpper(snakeCase(viewType))}_SELECTED_ID`;

    return USER_ENV_KEYS[key] ?? key;
  };

  const getLastSelectedOverviewInfo = <T extends string>({
    dbType,
    viewType,
  }: {
    dbType?: string | null;
    viewType?: string | null;
  }): LastSelectedInfoByViewType<T> | undefined => {
    if (!isValidDbType(dbType) || !isValidViewType(viewType)) {
      return undefined;
    }

    return lastSelected.value[getOverviewSelectedIdKey({ dbType, viewType })];
  };

  const getLastSelectedOverviewId = ({
    dbType,
    viewType,
  }: {
    dbType?: string | null;
    viewType?: string | null;
  }) => {
    if (!isValidDbType(dbType) || !isValidViewType(viewType)) {
      return '';
    }

    switch (viewType) {
      case OVERVIEW_TYPE.SINGLE_VIEW:
      case OVERVIEW_TYPE.CLUSTER_VIEW:
        return (
          getLastSelectedOverviewInfo<typeof OVERVIEW_TYPE.SINGLE_VIEW>({ dbType, viewType })
            ?.instanceId ?? ''
        );
      case OVERVIEW_TYPE.MULTI_VIEW:
        return (
          getLastSelectedOverviewInfo<typeof OVERVIEW_TYPE.MULTI_VIEW>({ dbType, viewType })
            ?.folderId ?? ''
        );
      default:
        return '';
    }
  };

  const getLastInstances = ({ dbType }: { dbType?: string | null }) => {
    if (!isValidDbType(dbType)) {
      return [];
    }

    return (
      getLastSelectedOverviewInfo<typeof OVERVIEW_TYPE.MULTI_VIEW>({
        dbType,
        viewType: OVERVIEW_TYPE.MULTI_VIEW,
      })?.instances?.filter(getEnabledInstance(dbType)) ?? []
    );
  };

  const getLastInstanceIds = ({ dbType }: { dbType?: string | null }) => {
    if (!isValidDbType(dbType)) {
      return [];
    }

    return getLastInstances({ dbType })?.map((instance) => instance.instanceId!) ?? [];
  };

  const getIdxFromLastInstanceIds = ({
    dbType,
    instanceId,
  }: {
    dbType?: string | null;
    instanceId: string;
  }) => {
    return getLastInstanceIds({ dbType }).findIndex((value) => value === instanceId);
  };

  const setGroupInfo = async ({
    info,
    dbType,
  }: {
    info: LastSelectedMultiView;
    dbType: NonNullable<DBType>;
  }) => {
    const groupId = info.folderId;
    if (isNil(groupId)) {
      return;
    }

    try {
      const groupInfo = useDatabaseMetaStore().getInstanceGroupById(groupId) ?? {
        folderId: '',
        name: '',
        description: '',
        instances: [],
      };

      const envKey = getOverviewSelectedIdKey({ dbType, viewType: OVERVIEW_TYPE.MULTI_VIEW });
      lastSelected.value[envKey] = groupInfo;

      await setUserEnv<LastSelectedMultiView>({
        key: envKey,
        value: {
          folderId: groupId,
        },
      });
    } catch (e: unknown) {
      console.log(e);
    }
  };

  const setInstanceInfo = async ({
    info,
    dbType,
    viewType,
  }: {
    info: LastSelectedSingleView;
    dbType: NonNullable<DBType>;
    viewType: InstanceInfoViewType;
  }) => {
    const { folderId: groupId, instanceId } = info;
    if (isNil(groupId) || !instanceId) {
      return;
    }
    try {
      const instanceInfo = useDatabaseMetaStore().getInstancesById({
        groupId,
        instanceId,
      });

      const envKey = getOverviewSelectedIdKey({ dbType, viewType });
      lastSelected.value[envKey] = instanceInfo;

      await setUserEnv<LastSelectedSingleView>({
        key: envKey,
        value: {
          folderId: groupId,
          instanceId,
        },
      });
    } catch (e: unknown) {
      console.log(e);
    }
  };

  const setLastOverviewInfo = async ({ info, dbType, viewType }: LastSelectedInfo) => {
    if (!isValidDbType(dbType) || !isValidViewType(viewType)) {
      return;
    }

    switch (viewType) {
      case OVERVIEW_TYPE.SINGLE_VIEW:
      case OVERVIEW_TYPE.CLUSTER_VIEW:
        await setInstanceInfo({ info, dbType, viewType });
        return;
      case OVERVIEW_TYPE.MULTI_VIEW:
        await setGroupInfo({ info, dbType });
        return;
      default:
        return;
    }
  };

  const fetchLastInstanceInfo = async ({
    dbType,
    viewType,
  }: {
    dbType: NonNullable<DBType>;
    viewType: InstanceInfoViewType;
  }) => {
    try {
      const envKey = getOverviewSelectedIdKey({ dbType, viewType });

      const storedInfo = getUserEnv<LastSelectedSingleView>(envKey) ?? {
        folderId: '',
        instanceId: '',
      };

      const {
        folderId: groupId,
        instanceId,
        selectedInfo,
      } = getRtmSelectedInfo({
        idInfo: storedInfo,
        folderList: useDatabaseMetaStore().getInstanceGroups(),
        dbType,
        hasInstanceId: true,
      });

      // userEnv에서 조회한 instanceId와 실제 화면에 보여져야 할 instanceId가 다른 경우
      // e.g. userEnv에서는 A라는 인스턴스가 저장되어 있지만 A라는 인스턴스가 disable로 변경된 상태인 경우
      if (storedInfo.instanceId !== instanceId) {
        await setInstanceInfo({
          info: {
            folderId: groupId!,
            instanceId: instanceId!,
          },
          dbType,
          viewType,
        });
        return;
      }

      lastSelected.value[envKey] = selectedInfo;
    } catch (e: unknown) {
      console.log(e);
    }
  };

  const fetchLastGroupInfo = async (dbType: NonNullable<DBType>) => {
    try {
      const envKey = getOverviewSelectedIdKey({ dbType, viewType: OVERVIEW_TYPE.MULTI_VIEW });

      const storedInfo = getUserEnv<LastSelectedMultiView>(
        getOverviewSelectedIdKey({ dbType, viewType: OVERVIEW_TYPE.MULTI_VIEW }),
      ) ?? {
        folderId: '',
      };

      const { folderId: groupId, selectedInfo } = getRtmSelectedInfo({
        idInfo: storedInfo,
        folderList: useDatabaseMetaStore().getInstanceGroups(),
        dbType,
        hasInstanceId: false,
      });

      // userEnv에서 조회한 folderId와 실제 화면에 보여져야 할 folderId가 다른 경우
      // e.g. userEnv에서는 A라는 폴더가 저장되어 있지만 A라는 폴더가 삭제된 경우
      if (storedInfo.folderId !== groupId) {
        await setGroupInfo({
          info: {
            folderId: groupId!,
          },
          dbType,
        });
        return;
      }

      lastSelected.value[envKey] = selectedInfo;
    } catch (e: unknown) {
      console.log(e);
    }
  };

  const fetchLastOverviewInfo = async ({
    dbType,
    viewType,
  }: {
    dbType: NonNullable<DBType>;
    viewType: OverviewType;
  }) => {
    if (!isValidDbType(dbType) || !isValidViewType(viewType)) {
      return;
    }

    switch (viewType) {
      case OVERVIEW_TYPE.SINGLE_VIEW:
      case OVERVIEW_TYPE.CLUSTER_VIEW:
        await fetchLastInstanceInfo({ dbType, viewType });
        return;
      case OVERVIEW_TYPE.MULTI_VIEW:
        await fetchLastGroupInfo(dbType);
        return;
      default:
        return;
    }
  };

  return {
    initLastSelected,
    getLastSelectedOverviewInfo,
    getLastSelectedOverviewId,
    getLastInstances,
    getLastInstanceIds,
    getIdxFromLastInstanceIds,
    setLastOverviewInfo,
    fetchLastOverviewInfo,
  };
};

export const useSingleStoreClusterInstance = () => {
  const clusterDiffKey = CLUSTER_DIFF_KEY[DB_TYPE.SINGLESTORE]!;

  const getClusterInstances = (): InstanceInfo[] => {
    const allInstances =
      useDatabaseMetaStore()
        .getInstanceGroupsByDbType(DB_TYPE.SINGLESTORE)
        .flatMap((group) => group.instances?.filter((instance) => instance.enabled) ?? []) ?? [];

    const clusterInstances = allInstances.filter((aInstance) => {
      if (!aInstance) {
        return false;
      }

      return (
        aInstance.enabled &&
        aInstance.instanceId === aInstance[clusterDiffKey] &&
        allInstances.filter(
          (bInstance) => !!bInstance && aInstance.instanceId === bInstance[clusterDiffKey],
        ).length > 1
      );
    });

    return clusterInstances;
  };

  const getInstancesByClusterId = (clusterId?: string | null): InstanceInfo[] => {
    if (!clusterId) {
      return [];
    }

    const groups = useDatabaseMetaStore().getInstanceGroupsByDbType(DB_TYPE.SINGLESTORE);

    const targetGroup = groups.find((group) =>
      group.instances?.some((instance) => instance.instanceId === clusterId && instance.enabled),
    );

    return (
      targetGroup?.instances?.filter(
        (instance) => instance.instanceId !== clusterId && instance[clusterDiffKey] === clusterId,
      ) ?? []
    );
  };

  const getInstanceIdsByClusterId = (clusterId?: string | null): string[] => {
    if (!clusterId) {
      return [];
    }

    return getInstancesByClusterId(clusterId).map((instance) => instance.instanceId!);
  };

  return {
    getInstancesByClusterId,
    getInstanceIdsByClusterId,
    getClusterInstances,
  };
};

export const useOverviewLoading = (apiKey: string) => {
  const overviewStore = useDatabaseOverviewStore();
  const { currentViewInfo } = storeToRefs(overviewStore);

  const fetchingCount = useIsFetching({ queryKey: [apiKey] });
  const mutatingCount = useIsMutating({ mutationKey: [apiKey] });

  const isInitialLoadComplete = ref(false);

  const isLoading = computed(() => {
    if (isInitialLoadComplete.value) {
      return false;
    }
    return fetchingCount.value > 0 || mutatingCount.value > 0;
  });

  watch([fetchingCount, mutatingCount], ([fetching, mutating]) => {
    if (!isInitialLoadComplete.value && fetching === 0 && mutating === 0) {
      isInitialLoadComplete.value = true;
    }
  });

  watch(
    () => currentViewInfo.value.queryId,
    (currQueryId, prevQueryId) => {
      if (currQueryId !== prevQueryId) {
        isInitialLoadComplete.value = false;
      }
    },
  );

  return {
    isLoading,
  };
};

export const useOverviewAnimation = () => {
  const INTERVAL_ANIMATION_SWITCH = 1000 * 7;

  const animationTimer = ref();
  const { t } = useInternational();
  const { showInfoMsg } = useMessage();

  // Grouping Summary 영역 애니메이션 관련
  const animationFlag = ref<boolean>(false); // 차트 <-> 그리드 교차 animation flag (5초)
  const isLockAnimation = ref<boolean>(false); // animation lock 여부

  const animateFrame = () => {
    if (!isLockAnimation.value) {
      animationFlag.value = !animationFlag.value;
      animationTimer.value = setTimeout(animateFrame, INTERVAL_ANIMATION_SWITCH);
    } else {
      clearTimeout(animationTimer.value);
    }
  };

  const onClickOption = () => {
    isLockAnimation.value = !isLockAnimation.value;
    showInfoMsg({
      msgStr: isLockAnimation.value ? t('NOTI.UI.ANIMATION_OFF') : t('NOTI.UI.ANIMATION_ON'),
    });
    animateFrame();
  };

  onMounted(() => {
    animateFrame();
  });

  onBeforeUnmount(() => {
    if (animationTimer?.value) {
      clearTimeout(animationTimer.value);
    }
  });

  const visibilityHandler = () => {
    if (document.hidden) {
      if (animationTimer?.value) {
        clearTimeout(animationTimer.value);
      }
    } else {
      animateFrame();
    }
  };
  onBeforeMount(() => {
    document.addEventListener('visibilitychange', visibilityHandler);
  });
  onBeforeUnmount(() => {
    document.removeEventListener('visibilitychange', visibilityHandler);
  });

  const ADMIN_REF_INDEX = {
    VACUUM: 0,
    ADDITIONAL_INFO: 1,
    ALERT_LOGS: 2,
  };

  // Status Stat - Alarm 연계
  const adminReferenceOpenedItemIdx = ref<number>(ADMIN_REF_INDEX.VACUUM);
  const onClickAlertStatus = () => {
    adminReferenceOpenedItemIdx.value = ADMIN_REF_INDEX.ALERT_LOGS;
  };

  return {
    animationFlag,
    adminReferenceOpenedItemIdx,
    isLockAnimation,

    onClickOption,
    onClickAlertStatus,
  };
};

export const useOverviewChangeCleanup = ({
  dbType,
  viewType,
  cleanup,
}: {
  dbType: string;
  viewType: string;
  cleanup: () => void;
}) => {
  const overviewId = computed<string>(() =>
    useDatabaseOverviewStore().getLastSelectedOverviewId({ dbType, viewType }),
  );

  watch(overviewId, () => {
    cleanup();
  });

  onBeforeUnmount(() => {
    cleanup();
  });
};

export const useGetOverviewExcelPeriod = () => {
  const { selectedPeriodInfo } = storeToRefs(useDatabaseOverviewStore());
  const lastFetchTime = ref<OverviewTimePeriod>({ fromTime: '', toTime: '' });

  const setLastFetchTime = (
    query?: Partial<TimeRangeInfo>,
    period?: NonNullable<MetricRequest['period']>,
  ) => {
    if (query && query.fromTime && query.toTime) {
      lastFetchTime.value = {
        fromTime: query.fromTime,
        toTime: query.toTime,
      };
    } else {
      lastFetchTime.value = getTimeRangeFromPeriod(
        period ?? 'p5s',
        selectedPeriodInfo.value.toTimeUtc,
      );
    }
  };

  return {
    lastFetchTime,

    setLastFetchTime,
  };
};
